import { notification } from 'antd';
import fetch from './fetch';
import { BASE_URL } from '../config';
import axiosConfig from './axiosConfig';
import { get } from 'lodash';

export const saveUsers = async (userDetail) => {
  try {
    const res = await axiosConfig.post(`/cognito/user`, userDetail);

    if (res.status === 403) {
      notification.error({
        message: 'Unauthorized',
      });
      return { status: 403, error: 'Unauthorized' };
    }
    if (res.status === 200) {
      // Return full response data to access password when invite email is not sent
      // Handle both response structures:
      // Success: { "message": "success", "data": { "User": {...} } }
      // Error: { "error": "User with the given email already exists." }
      const responseData = res.data;

      // Check if it's an error response
      if (responseData?.error) {
        return {
          status: 400,
          error: responseData.error,
        };
      }

      // Return success response with data
      return {
        status: res.status,
        data: responseData?.data || responseData,
        message: responseData?.message,
      };
    }
    if (res.status === 502) {
      return { status: 502, error: 'Bad Gateway' };
    }
    return { status: res.status, error: 'Unknown error' };
  } catch (error) {
    // Return error information so frontend can display proper error message.
    // Interceptor may reject with response `data` object directly: { error: "..." }.
    const errorMessage =
      error?.response?.data?.error ||
      (typeof error === 'object' && error !== null && 'error' in error ? error.error : null) ||
      error?.message ||
      'An error occurred while creating the user';
    return {
      status: error?.response?.status || 400,
      error: typeof errorMessage === 'string' ? errorMessage : String(errorMessage),
    };
  }
};

export const addAccess = async (userDetail) => {
  try {
    const res = await axiosConfig.put(
      `/cognito/user/add-to-group/${userDetail.userName}?group=${userDetail.group}`
    );
    if (res.status === 403) {
      notification.error({
        message: 'Unauthorized',
      });
    }
    if (res.status === 200) {
      return res.status;
    }
    if (res.status === 502) {
      return res.status;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const removeAccess = async (userDetail) => {
  try {
    const res = await axiosConfig.put(
      `/cognito/user/remove-from-group/${userDetail.userName}?group=${userDetail.group}`
    );
    if (res.status === 403) {
      notification.error({
        message: 'Unauthorized',
      });
      return null;
    }
    if (res.status === 200) {
      return res.status;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const resetUserPassword = async (userDetail) => {
  try {
    const res = await axiosConfig.put(`/cognito/user/set-password/${userDetail.userName}`, {
      password: userDetail.password,
      email: userDetail.email,
    });
    if (res.status === 403) {
      notification.error({
        message: 'Unauthorized',
      });
      return null;
    }
    if (res.status === 200) {
      return true;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const removeUser = async (userName, email) => {
  try {
    const res = await axiosConfig.delete(`/cognito/user/${userName}?email=${email}`);
    if (res.status === 403) {
      notification.error({
        message: 'Unauthorized',
      });
      return null;
    }
    if (res.status === 200) {
      return true;
    }
    return null;
  } catch (error) {
    return null;
  }
};

// export const sendUserEmail = async (userDetail) => {
//   try {
//     const res = await fetch(`${BASE_URL}/sendLoginDetails`, {
//       method: 'POST',
//       body: JSON.stringify(userDetail),
//       headers: {
//         Accept: 'application/json',
//       },
//     });
//     if (res.status === 403) {
//       notification.error({
//         message: 'Unauthorized',
//       });
//       return null;
//     } if (res.status === 200) {
//       return true;
//     }
//     return null;
//   } catch (error) {
//     return null;
//   }
// };

export const sendUserEmail = async (userDetail) => {
  try {
    const res = await axiosConfig.post(`/cognito/user/sendLoginDetails`, userDetail);
    if (res.status === 403) {
      notification.error({
        message: 'Unauthorized',
      });
      return null;
    }
    if (res.status === 200) {
      return true;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const getUsers = async (pageToken, pih) => {
  try {
    let url = `/cognito/users?paginationToken=${pageToken}&searchKey=${pih}`;
    const res = await axiosConfig.get(url);
    if (res.status === 403) {
      notification.error({
        message: 'Unauthorized',
      });
      return null;
    }
    if (res.status === 200) {
      const usersData = get(res, ['data', 'data'], {});
      return usersData;
    }
  } catch (error) {
    return null;
  }
};

export const getAccess = async (userName) => {
  try {
    let url = `/cognito/user/groups/${userName}`;
    const res = await axiosConfig.get(url);
    if (res.status === 403) {
      notification.error({
        message: 'Unauthorized',
      });
      return null;
    }
    if (res.status === 200) {
      const usersData = get(res, ['data', 'data'], {});
      return usersData;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const resetPasswordCredentials = async (username, email, creatorEmail) => {
  try {
    const res = await axiosConfig.post(`/cognito/user/reset-password-credentials/${username}`, {
      email: email,
      creatorEmail: creatorEmail,
    });
    if (res.status === 403) {
      notification.error({
        message: 'Unauthorized',
      });
      return null;
    }
    if (res.status === 200) {
      return res.data?.data || res.data;
    }
    return null;
  } catch (error) {
    console.error('Error resetting password credentials:', error);
    return null;
  }
};
